import { useState } from "react";
import { formatItalianDate, personName } from "../../format";
import type { NecrologioDTO } from "../../../api/contract";
import { ApiRequestError, discardNecrologio, restoreNecrologio } from "../../api";
import { useSession } from "../../auth-client";
import { Badge, Btn, Drawer } from "../../ui";
import { MatchHistoryPanel } from "./MatchHistoryPanel";
import { isPlatformAdminRole } from "../../../api/platform-roles";

interface NecrologioDrawerProps {
  selected: NecrologioDTO | null;
  provincia: string;
  browserQueueBusy: boolean;
  onClose: () => void;
  onEnqueue: (scopeProvincia: string | null) => void;
  onUpdated: (next: NecrologioDTO) => void;
}

export function NecrologioDrawer({
  selected,
  provincia,
  browserQueueBusy,
  onClose,
  onEnqueue,
  onUpdated,
}: NecrologioDrawerProps) {
  const { data: session } = useSession();
  const isPlatformAdmin = isPlatformAdminRole(session?.user?.role);
  const [triageBusy, setTriageBusy] = useState(false);
  const [triageErr, setTriageErr] = useState<string | null>(null);
  const discarded = selected?.discardedAt != null;

  async function runDiscard() {
    if (!selected) return;
    setTriageBusy(true);
    setTriageErr(null);
    try {
      const next = await discardNecrologio(selected.id);
      onUpdated(next);
    } catch (e) {
      setTriageErr(e instanceof ApiRequestError ? e.message : "Errore scarto");
    } finally {
      setTriageBusy(false);
    }
  }

  async function runRestore() {
    if (!selected) return;
    setTriageBusy(true);
    setTriageErr(null);
    try {
      const next = await restoreNecrologio(selected.id);
      onUpdated(next);
    } catch (e) {
      setTriageErr(e instanceof ApiRequestError ? e.message : "Errore ripristino");
    } finally {
      setTriageBusy(false);
    }
  }

  return (
    <Drawer
      open={!!selected}
      onClose={onClose}
      title={selected ? personName(selected.nome) : ""}
      sub={
        selected
          ? [
            selected.dataDecesso ? `decesso ${formatItalianDate(selected.dataDecesso)}` : null,
            selected.eta ? `${selected.eta} anni` : "età non indicata",
            selected.annoNascita ? `nascita ${selected.annoNascita} circa` : null,
            selected.comune || selected.luogo || null,
          ].filter(Boolean).join(" · ")
          : ""
      }
    >
      {selected && (
        <div className="col gap-12">
          <div className="row gap-6" style={{ flexWrap: "wrap" }}>
            <Badge kind="accent">{selected.source}</Badge>
            {selected.provincia && <Badge kind="muted">{selected.provincia}</Badge>}
            {discarded && <Badge kind="muted">scartato</Badge>}
            {selected.vedovo && (
              <Badge kind="info" title="Necrologio con ved./vedovo/vedova rilevato">ved.</Badge>
            )}
            {selected.impresaFunebre && (
              <Badge kind="muted">
                {selected.impresaFunebre}
                {selected.telefonoImpresa && ` · ${selected.telefonoImpresa}`}
              </Badge>
            )}
            {selected.link && (
              <a
                href={selected.link}
                target="_blank"
                rel="noreferrer"
                className="chip mono"
                style={{ fontSize: 11, color: "var(--accent)" }}
              >
                apri originale ↗
              </a>
            )}
          </div>
          {selected.contenuto && (
            <div
              className="preview-box"
              style={{
                fontSize: 12,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
              }}
            >
              {selected.contenuto}
            </div>
          )}
          <div className="preview-box" style={{ padding: "12px 14px", lineHeight: 1.5 }}>
            <div className="row gap-8" style={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
              <div>
                <div className="k" style={{ fontWeight: 600 }}>
                  {discarded ? "Scartato dalla lista attiva" : "Prossimo passo"}
                </div>
                <div style={{ marginTop: 4 }}>
                  {discarded
                    ? "Non compare più in Accoda né nella lista di default. Ripristina per rivederlo."
                    : `Verifica SISTER con ${selected.provincia ? `provincia ${selected.provincia}` : "provincia da necrologio"}.`}
                </div>
              </div>
              <div className="row gap-6" style={{ flexWrap: "wrap" }}>
                {!discarded && (
                  <Btn
                    size="sm"
                    variant="primary"
                    disabled={browserQueueBusy}
                    onClick={() => onEnqueue(selected.provincia || provincia || null)}
                  >
                    {browserQueueBusy ? "Invio…" : "Cerca su SISTER…"}
                  </Btn>
                )}
                {isPlatformAdmin && (discarded ? (
                  <Btn size="sm" variant="ghost" disabled={triageBusy} onClick={() => void runRestore()}>
                    {triageBusy ? "…" : "Ripristina"}
                  </Btn>
                ) : (
                  <Btn
                    size="sm"
                    variant="ghost"
                    disabled={triageBusy}
                    title="Nasconde dalla lista attiva e da Accoda (non cancella)"
                    onClick={() => void runDiscard()}
                  >
                    {triageBusy ? "…" : "Scarta"}
                  </Btn>
                ))}
              </div>
            </div>
            {triageErr && (
              <div style={{ marginTop: 8, color: "var(--danger)", fontSize: 12 }}>{triageErr}</div>
            )}
          </div>
          <MatchHistoryPanel
            necrologioId={selected.id}
            onEnqueue={() => onEnqueue(selected.provincia || provincia || null)}
            enqueueBusy={browserQueueBusy}
          />
        </div>
      )}
    </Drawer>
  );
}
